import { AfterViewInit, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';
import { Subject } from 'app/shared/models/subject';
import { Utilities } from 'app/shared/helpers/utilities';
import ForceGraph, { ForceGraphInstance } from 'force-graph';
import { DxContextMenuComponent, DxPopupComponent } from 'devextreme-angular';
import { Router } from '@angular/router';
import { ELNode, ELVisData, SubjectVisualizerService } from 'app/shared/services/subject-visualizer.service';

@Component({
  selector: 'subject-visualizer-dialog',
  templateUrl: './subject-visualizer-dialog.component.html',
  styleUrls: [
    './subject-visualizer-dialog.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class SubjectVisualizerDialogComponent implements OnInit, AfterViewInit {

  isPopupVisible: boolean = true;

  title: string;
  subject: Subject;
  vizData: any = null;
  divGraph = null;
  highlightNodes = new Set();
  highlightLinks = new Set();
  hoverNode = null;
  nodeLocationsChanged: boolean = false;
  myGraph: ForceGraphInstance = null;
  CurrentZoom: number;
  CurrentPanX: number;
  CurrentPanY: number;
  CurrentWidth: number;
  CurrentHeight: number;
  popupWidth: number = 800;
  popupHeight: number = 500;
  bFirstTime: boolean = true;
  bLoading: boolean = false;
  @ViewChild(DxContextMenuComponent) ctxMenu: DxContextMenuComponent;
  @ViewChild('popupSubjectVisualizer') popup: DxPopupComponent;
  menuItems = [];

  canvasPaddingX: number = 20 + 7 + 7 + 20; // Canvas Padding + Left Margin + Right Margin
  canvasPaddingY: number = 63 + 20 + 20;      // Title Height + Canvas Padding

  constructor(
    public dialogRef: MatDialogRef<SubjectVisualizerDialogComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public router: Router,
    private subjectVisualizerService: SubjectVisualizerService,
  ) { }

  ngOnInit() {
    this.subject = this.data.subject;
    this.vizData = this.data.vizData;

    if (!this.data.vizData) {
      this.bLoading = true;
      this.subjectVisualizerService.getSubjectVizData(this.subject.id).subscribe(
        (res: ELVisData) => {
          this.bLoading = false;
          this.vizData = res;
          this.showVisNetwork();
        }
      )
    }

    const subjectTypeName = (this.subject.isReferral && (this.subject.referral)) ? 'Referral' : this.subject.subjectType.name;
    this.title = `Visualise Subject Links for ${subjectTypeName}: ${this.subject.summary}`;

    this.showVisNetwork = this.showVisNetwork.bind(this);
    this.ShowContextMenu = this.ShowContextMenu.bind(this);
    this.nodeHover = this.nodeHover.bind(this);
    this.linkHover = this.linkHover.bind(this);
    this.nodeEndDrag = this.nodeEndDrag.bind(this);
    this.nodePaintAsText = this.nodePaintAsText.bind(this);
    this.nodePointerAreaPaintAsText = this.nodePointerAreaPaintAsText.bind(this);
    this.ZoomEnd = this.ZoomEnd.bind(this);
    this.onResizeStart = this.onResizeStart.bind(this);
    this.onResizeEnd = this.onResizeEnd.bind(this);
  }

  resizeInProgress:boolean=false;
  onResizeStart(e) {
    this.saveLayout();
    this.resizeInProgress=true;
  }

  onResizeEnd(e) {
    this.popupWidth = e.width;
    this.popupHeight = e.height;
    this.showVisNetwork(e.width, e.height, true);
    this.resizeInProgress=false;
  }

  ngAfterViewInit(): void {
    this.divGraph = document.querySelector('#divGraph');

    // Do this once after view init so we can get div graph element which is needed
    this.showVisNetwork();

    Utilities.patchCheckboxAria();
  }

  CloseDialog() {
    this.saveLayout();
    this.isPopupVisible = false;
    this.dialogRef.close()
  }
  showVisNetwork(width: number = 800, height: number = 500, override: boolean = false) {
    if (!this.vizData)
      return;

    this.bLoading = true;

    // Restore popup size from data providing override is false.
    if (this.vizData.width && this.vizData.height && this.popup && override == false) {
      width = this.vizData.width;
      height = this.vizData.height;
    }

    // Set Popup size
    this.popup.width = width;
    this.popup.height = height;

    // Save Popup size
    this.popupWidth = width;
    this.popupHeight = height;

    // Calc canvas size
    this.CurrentWidth = width - this.canvasPaddingX;
    this.CurrentHeight = height - this.canvasPaddingY;

    var myData =
    {
      nodes: this.vizData.nodes,
      links: this.vizData.links,
    };
    // https://github.com/vasturiano/force-graph
    if (!this.myGraph)
    {
      this.myGraph = ForceGraph();
      this.myGraph.resetProps();    
      this.myGraph(this.divGraph)
      .width(this.CurrentWidth)
      .height(this.CurrentHeight)
      .backgroundColor("White") // Use white background, important for when canvas is saved.
      .graphData(myData)
      .nodeId('subjectId')
      .nodeLabel('summary')
      .nodeColor('color')
      .linkSource('parentSubjectId')
      .linkTarget('childSubjectId')
      .linkLabel('relationshipName')
      .linkColor('color')
      .minZoom(1.25)
      .maxZoom(8)

      .autoPauseRedraw(false) // keep redrawing after engine has stopped.  This is so that node/links highlighting works
      .cooldownTime(2000)     // Limit layout time to 2 seconds
      .linkWidth(link => this.highlightLinks.has(link) ? 3 : 1)
      .linkDirectionalParticles(4)
      .linkDirectionalParticleWidth(link => this.highlightLinks.has(link) ? 2 : 0)

      .nodeCanvasObject((node, ctx, globalScale) => this.nodePaintAsText(node, ctx, globalScale))
      .nodePointerAreaPaint(this.nodePointerAreaPaintAsText)

      .onLinkHover(link => this.linkHover(link))
      .onNodeHover(node => this.nodeHover(node))
      .onNodeDragEnd(node => this.nodeEndDrag(node))
      .onNodeRightClick((node, event) => this.ShowContextMenu(node, event))
      .onBackgroundRightClick((event) => this.ShowContextMenu(null, event))
      .onBackgroundClick((event) => this.ShowContextMenu(null, null))
      .onZoomEnd(this.ZoomEnd)
      ;
    }
    else{
      this.myGraph(this.divGraph)
        .width(this.CurrentWidth)
        .height(this.CurrentHeight);
    }
    
    if (this.vizData.panX && this.vizData.panY) {
      this.myGraph.centerAt(this.vizData.panX, this.vizData.panY, 100);
    }
    if (this.vizData.zoom && this.myGraph.zoom()!=this.vizData.zoom){
        this.myGraph.zoom(this.vizData.zoom);
    }

    // Auto Fit content to window or restore Zoom and center using values
    this.myGraph.onEngineStop(() => {
      console.log("this.myGraph.onEngineStop");

      if (this.bFirstTime == true) {
        if (this.vizData.zoom) {
          console.log("this.myGraph.onEngineStop - Set Zoom");
          this.myGraph.zoom(this.vizData.zoom);
        }
        else {
          console.log("this.myGraph.onEngineStop - Auto Zoom");
          this.myGraph.zoomToFit(400, 10);
        }

        if (this.vizData.panX && this.vizData.panY) {
          console.log("this.myGraph.onEngineStop - Set Center");
          this.myGraph.centerAt(this.vizData.panX, this.vizData.panY, 100);
        }
        else {
          console.log("this.myGraph.onEngineStop - Auto Center");
          this.myGraph.d3Force('center', null);
        }
        this.bFirstTime = false;
      }
      this.bLoading = false;
    });
  }

  // Called when zoom or pan finishes
  ZoomEnd(d) {
    if (this.resizeInProgress===false)
    {
      this.CurrentZoom = d.k;
      this.CurrentPanX = d.x;
      this.CurrentPanY = d.y;
    }
  }

  nodePaintAsText(node, ctx, globalScale) {
    const label = node.subjectType + ': ' + node.summary;
    var fontSize = 12 / globalScale;
    if (fontSize < 6)
      fontSize = 6;
    ctx.font = `${fontSize}px Sans-Serif`;
    const textWidth = ctx.measureText(label).width;
    const bckgDimensions = [textWidth + fontSize, fontSize + fontSize]; // .map(n => n + (fontSize * 0.25)); 

    ctx.fillStyle = node.color; // 'rgba(255, 255, 255, 0.8)';
    ctx.fillRect(node.x - bckgDimensions[0] / 2, node.y - bckgDimensions[1] / 2, ...bckgDimensions);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillText(label, node.x, node.y);

    node.__bckgDimensions = bckgDimensions; // to re-use in nodePointerAreaPaint
  }

  nodePointerAreaPaintAsText(node, color, ctx) {
    ctx.fillStyle = color;
    const bckgDimensions = node.__bckgDimensions;
    bckgDimensions && ctx.fillRect(node.x - bckgDimensions[0] / 2, node.y - bckgDimensions[1] / 2, ...bckgDimensions);
  };

  nodeEndDrag(node) {
    node.fx = node.x;
    node.fy = node.y;
    this.nodeLocationsChanged = true;
  }

  nodeHover(node) {
    this.highlightNodes.clear();
    this.highlightLinks.clear();
    if (node) {
      this.highlightNodes.add(node);

      var lk = this.vizData.links.filter(i => i.parentSubjectId == node.subjectId || i.childSubjectId == node.subjectId);
      lk.forEach(link => this.highlightLinks.add(link));
    }

    this.hoverNode = node || null;
  }

  linkHover(link) {
    this.highlightNodes.clear();
    this.highlightLinks.clear();

    if (link) {
      this.highlightLinks.add(link);
      this.highlightNodes.add(link.source);
      this.highlightNodes.add(link.target);
    }
  }

  ShowContextMenu(node, event) {
    if (!node && !event) {
      this.ctxMenu.visible = false;
      return;
    }

    this.menuItems = [];
    if (node) {
      this.menuItems.push({ text: 'Navigate To :' + node.subjectType + ', ' + node.summary, command: 'navigateTo', data: node });
    }
    this.menuItems.push({ text: 'Refresh  Layout', command: 'refreshLayout', beginGroup: true });
    this.menuItems.push({ text: 'Save Layout', command: 'saveLayout' });
    this.menuItems.push({ text: 'Export as Image', command: 'exportAsImage' });
    this.ctxMenu.visible = true;
  }

  itemClick(e) {
    this.ctxMenu.visible = false;
    if (!e.itemData.items) {
      const node: ELNode = e.itemData.data as ELNode;
      switch (e.itemData.command) {
        case "navigateTo":
          // Close Dialog.
          this.CloseDialog();
          this.router.navigateByUrl('subjects/' + node.subjectType + '/' + node.uid);
          break;

        case "refreshLayout":
          // Remove location data from Nodes and redraw
          this.resetLayout();
          break;

        case "saveLayout":
          this.saveLayout();
          break;

        case "exportAsImage":
          this.ExportAsImage();
          break;
      }
    }
  }
  ExportAsImage() {
    // Get Canvas element
    var canvas: HTMLCanvasElement = this.divGraph.querySelector('div > canvas');

    // Create new canvas and set background to white.    
    var newCanvas = <HTMLCanvasElement>canvas.cloneNode(true);
    var ctx = newCanvas.getContext('2d');
    ctx.fillStyle = "#FFF";
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
    // Draw original image over the top as it has transparent background
    ctx.drawImage(canvas, 0, 0);

    // Create png data string for canvas
    let canvasUrl = newCanvas.toDataURL("image/png");

    // Create an anchor, and set the href value to our data URL
    const createEl = document.createElement('a');
    createEl.href = canvasUrl;

    // This is the name of our downloaded file    
    createEl.download = this.subject.subjectType.name + "-" + this.subject.summary + ".png";

    // Click the download button, causing a download, and then remove it
    createEl.click();
    createEl.remove();
  }

  // Reset layout
  resetLayout() {
    this.vizData.nodes.forEach(i => {
      i.x = undefined;
      i.y = undefined;
      i.vx = undefined;
      i.vy = undefined;
      i.fx = undefined;
      i.fy = undefined;
    });
    this.nodeLocationsChanged = false;
    this.showVisNetwork(this.popupWidth, this.popupHeight, true);
  }

  saveLayout() {
    this.subjectVisualizerService.saveLayout(this.vizData, this.CurrentZoom, this.CurrentPanX, this.CurrentPanY, this.popupWidth, this.popupHeight, false).subscribe(x => {
    });
  }
}

